import React, { useMemo } from "react";
import { ThreeEvent } from "@react-three/fiber";
import { observer } from "mobx-react-lite";
import * as THREE from "three";
import { FloorplannerStoreContext } from "../../../../store/floorplannerStore";
import { renderStore } from "../../../../store/renderStore";
import {
  DoorType,
  DoubleDoorType,
  SlidingDoorType,
  SymbolType,
  WallType,
  WindowType,
  VoidType,
  isDoubleDoorType,
  isSingleLeafDoorType,
  isSlidingDoorType,
  isVoidType,
  isWindowType,
} from "../../../../types/floorplannerTypes";
import { resolveMaterial } from "../../materials/materialResolver";
import ExtrudedShapeMesh from "../../scene/ExtrudedShapeMesh";
import { usePointerUpSelection } from "../../scene/usePointerUpSelection";
import Door3DAssemblyMesh from "../../symbols/openings/doors/Door3DAssemblyMesh";
import SlidingDoor3DAssemblyMesh from "../../symbols/openings/doors/SlidingDoor3DAssemblyMesh";
import {
  DEFAULT_DOOR_HEIGHT,
  createDoor3DAssemblyGeometry,
  createWindow3DAssemblyGeometry,
  createOpeningFootprintShape,
  getSymbol3DBaseElevation,
} from "../../symbols/openings/openingGeometry";
import VoidOpening3D from "../../symbols/openings/voids/VoidOpening3D";
import WindowOpening3D from "../../symbols/openings/windows/WindowOpening3D";
import {
  getSymbol3DDefinition,
  isOpening3DDefinition,
} from "../../symbols/registry/symbol3DRegistry";
import {
  applyWallTextureUVs,
  getWallColor,
  getWallHeightAtDistance,
  getWallOpeningGeometryClippings,
  shouldUseIntegratedOpeningGeometry,
  WallEndpointHeights,
} from "./wallGeometry";
import Wall3DMaterial from "./Wall3DMaterial";

type HingedDoor3DSymbol = DoorType | DoubleDoorType;

const WallAttachment3DMeshes: React.FC<{
  wall: WallType;
  symbols: SymbolType[];
  wallHeight: number;
  wallEndpointHeights?: WallEndpointHeights;
  selected: boolean;
  selectedSymbolIds: Set<string>;
  cameraHidden: boolean;
  claimSelectionEvents?: boolean;
  onSelectWall: (wall: WallType, event: ThreeEvent<PointerEvent>) => void;
  onSelectSymbol: (symbol: SymbolType, event: ThreeEvent<PointerEvent>) => void;
  onOpeningAngleDragActiveChange: (active: boolean) => void;
}> = observer(
  ({
    wall,
    symbols,
    wallHeight,
    wallEndpointHeights,
    selected,
    selectedSymbolIds,
    cameraHidden,
    claimSelectionEvents = false,
    onSelectWall,
    onSelectSymbol,
    onOpeningAngleDragActiveChange,
  }) => {
    const floorplannerStore = React.useContext(FloorplannerStoreContext);
    const wallSelectionHandlers = usePointerUpSelection(wall, onSelectWall, {
      claimPointerEvents: claimSelectionEvents,
    });
    const configureWallTextureUVs = React.useCallback(
      (geometry: THREE.ExtrudeGeometry, positionZ: number) => {
        applyWallTextureUVs(geometry, wall, positionZ);
      },
      [wall],
    );
    const symbolsById = useMemo(
      () => new Map(symbols.map((symbol) => [symbol.id, symbol])),
      [symbols],
    );
    if (cameraHidden) return null;
    const wallClippings = renderStore.wallClippingsMap.get(wall.id);
    const combinedWallClippings = getWallOpeningGeometryClippings(
      wall,
      floorplannerStore.wallsMap.values(),
      floorplannerStore.wallWidth,
      wallClippings,
    );
    const attachedOpeningSymbols = (wall.symbolAttachments ?? []).map(
      (attachment) => symbolsById.get(attachment.symbolId),
    );
    const wallHasIntegratedOpeningGeometry = shouldUseIntegratedOpeningGeometry(
      wall,
      renderStore.innerWallShapesMap.get(wall.id)?.[0],
      combinedWallClippings,
      attachedOpeningSymbols,
    );

    return (
      <>
        {wall.symbolAttachments?.map((attachment) => {
          const symbol = symbolsById.get(attachment.symbolId);
          if (!symbol || !isStructureAttachment(symbol)) return null;
          const definition = getSymbol3DDefinition(symbol);
          if (!isOpening3DDefinition(definition)) return null;
          const symbolSelected = selectedSymbolIds.has(symbol.id);
          const attachmentWallHeight = wallEndpointHeights
            ? getWallHeightAtDistance(
                wall,
                wallEndpointHeights,
                attachment.positionFromStart,
              )
            : wallHeight;

          const wallWidth = wall.wallWidth ?? floorplannerStore.wallWidth;
          const defaultFrameWidth = isWindowType(symbol)
            ? floorplannerStore.windowFrameWidth
            : floorplannerStore.doorFrameWidth;
          const shape = createOpeningFootprintShape(
            wall,
            symbol,
            attachment.positionFromStart,
            defaultFrameWidth,
            wallWidth,
          );
          if (!shape) return null;

          if (definition.kind === "void" && isVoidType(symbol)) {
            const baseElevation = THREE.MathUtils.clamp(
              getSymbol3DBaseElevation(symbol),
              0,
              attachmentWallHeight,
            );
            const voidHeight = Math.max(
              Math.min(
                symbol.threeD?.height ?? attachmentWallHeight - baseElevation,
                attachmentWallHeight - baseElevation,
              ),
              0,
            );
            const voidTop = Math.min(
              baseElevation + voidHeight,
              attachmentWallHeight,
            );
            const lintelHeight = Math.max(
              attachmentWallHeight - voidTop,
              0,
            );
            const wallMassColor = resolveMaterial("wall", wall, {
              defaultColor: getWallColor(wall),
            }).color;

            return (
              <React.Fragment key={symbol.id}>
                {!wallHasIntegratedOpeningGeometry && baseElevation > 0.01 && (
                  <ExtrudedShapeMesh
                    shape={shape}
                    depth={baseElevation}
                    selected={selected}
                    receiveShadow
                    configureGeometry={configureWallTextureUVs}
                    {...wallSelectionHandlers}
                    userData={{
                      floorplannerType: "wall",
                      floorplannerId: wall.id,
                    }}
                  >
                    <Wall3DMaterial wall={wall} color={wallMassColor} />
                  </ExtrudedShapeMesh>
                )}
                {!wallHasIntegratedOpeningGeometry && lintelHeight > 0.01 && (
                  <ExtrudedShapeMesh
                    shape={shape}
                    depth={lintelHeight}
                    positionZ={voidTop}
                    selected={selected}
                    receiveShadow
                    configureGeometry={configureWallTextureUVs}
                    {...wallSelectionHandlers}
                    userData={{
                      floorplannerType: "wall",
                      floorplannerId: wall.id,
                    }}
                  >
                    <Wall3DMaterial wall={wall} color={wallMassColor} />
                  </ExtrudedShapeMesh>
                )}
                <VoidOpening3D
                  symbol={symbol as VoidType}
                  selected={symbolSelected}
                  shape={shape}
                  wallHeight={attachmentWallHeight}
                  claimSelectionEvents={claimSelectionEvents}
                  onSelectSymbol={onSelectSymbol}
                />
              </React.Fragment>
            );
          }

          if (definition.kind === "window" && isWindowType(symbol)) {
            const windowAssembly = createWindow3DAssemblyGeometry(
              wall,
              symbol as WindowType,
              attachment.positionFromStart,
              {
                defaultFrameWidth,
                wallWidth,
                wallHeight: attachmentWallHeight,
              },
            );
            return (
              <WindowOpening3D
                key={symbol.id}
                wall={wall}
                symbol={symbol as WindowType}
                assembly={windowAssembly}
                selected={symbolSelected}
                shape={shape}
                wallHeight={attachmentWallHeight}
                wallSelected={selected}
                renderWallMass={!wallHasIntegratedOpeningGeometry}
                claimSelectionEvents={claimSelectionEvents}
                onSelectWall={onSelectWall}
                onSelectSymbol={onSelectSymbol}
                onAngleDragActiveChange={onOpeningAngleDragActiveChange}
              />
            );
          }

          if (
            definition.kind !== "door" &&
            definition.kind !== "slidingDoor" &&
            definition.kind !== "doubleDoor"
          ) {
            return null;
          }

          const doorHeight = Math.min(
            symbol.threeD?.height ?? DEFAULT_DOOR_HEIGHT,
            attachmentWallHeight,
          );
          const lintelStart = Math.min(doorHeight, attachmentWallHeight);
          const lintelHeight = Math.max(
            attachmentWallHeight - lintelStart,
            0,
          );
          const doorAssembly = createDoor3DAssemblyGeometry(
            wall,
            symbol as DoorType | SlidingDoorType | DoubleDoorType,
            attachment.positionFromStart,
            {
              defaultFrameWidth,
              defaultBladeThickness: floorplannerStore.doorBladeThickness,
              wallWidth,
              doorHeight,
            },
          );
          const wallMassColor = resolveMaterial("wall", wall, {
            defaultColor: getWallColor(wall),
          }).color;

          return (
            <React.Fragment key={symbol.id}>
              {!wallHasIntegratedOpeningGeometry && lintelHeight > 0.01 && (
                <ExtrudedShapeMesh
                  shape={shape}
                  depth={lintelHeight}
                  positionZ={lintelStart}
                  selected={selected}
                  receiveShadow
                  configureGeometry={configureWallTextureUVs}
                  {...wallSelectionHandlers}
                  userData={{
                    floorplannerType: "wall",
                    floorplannerId: wall.id,
                  }}
                >
                  <Wall3DMaterial wall={wall} color={wallMassColor} />
                </ExtrudedShapeMesh>
              )}
              {doorAssembly && isSlidingDoorType(symbol) && (
                <SlidingDoor3DAssemblyMesh
                  symbol={symbol as SlidingDoorType}
                  assembly={doorAssembly}
                  selected={symbolSelected}
                  claimSelectionEvents={claimSelectionEvents}
                  onSelect={onSelectSymbol}
                  onAngleDragActiveChange={onOpeningAngleDragActiveChange}
                />
              )}
              {doorAssembly && !isSlidingDoorType(symbol) && (
                <Door3DAssemblyMesh
                  symbol={symbol as HingedDoor3DSymbol}
                  assembly={doorAssembly}
                  selected={symbolSelected}
                  claimSelectionEvents={claimSelectionEvents}
                  onSelect={onSelectSymbol}
                  onAngleDragActiveChange={onOpeningAngleDragActiveChange}
                />
              )}
            </React.Fragment>
          );
        })}
      </>
    );
  },
);

function isStructureAttachment(symbol: SymbolType): boolean {
  return (
    isSingleLeafDoorType(symbol) ||
    isDoubleDoorType(symbol) ||
    isWindowType(symbol) ||
    isVoidType(symbol)
  );
}

export default WallAttachment3DMeshes;
